import React, { useState } from 'react';
import { Modal, Form, Input, DatePicker, Select, Typography, Tag, Alert, Spin, message } from 'antd';

import { avaliacoesAdminService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Text } = Typography;

// "Nova avaliação" (ajuste pre-producao): o ADMIN escolhe as turmas já no
// momento da criação, em vez de criar período vazio e materializar
// turmas depois em telas separadas. Por baixo continua sendo
// PeriodoAvaliacao -> AvaliacaoTurma -> AvaliacaoAluno (arquitetura
// inalterada) -- este modal só simplifica a experiência, nunca expõe
// "materializar"/"snapshot"/"PENDENTE" ao usuário.
const NovaAvaliacaoModal = ({ open, onCancel, onCriada }) => {
  const [form] = Form.useForm();
  const [turmasCandidatas, setTurmasCandidatas] = useState([]);
  const [carregandoTurmas, setCarregandoTurmas] = useState(false);
  const [erroTurmas, setErroTurmas] = useState(null);
  const [criando, setCriando] = useState(false);

  const fecharEResetar = () => {
    form.resetFields();
    setTurmasCandidatas([]);
    setErroTurmas(null);
    onCancel();
  };

  // Ao definir a Data-base dos alunos, carrega as turmas candidatas --
  // preview por data_referencia solta, nenhum período é criado aqui.
  // Turma sem nenhum aluno elegível nunca aparece na lista (o backend já
  // filtra) -- não precisamos desabilitar opção nenhuma, só refletir o
  // que veio.
  const handleMudarDataReferencia = async (data) => {
    form.setFieldsValue({ turma_ids: [] });
    setTurmasCandidatas([]);
    setErroTurmas(null);
    if (!data) return;

    setCarregandoTurmas(true);
    try {
      const response = await avaliacoesAdminService.listarTurmasCandidatasParaCriacao(data.format('YYYY-MM-DD'));
      setTurmasCandidatas(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível carregar as turmas candidatas.');
      setErroTurmas(mensagem);
    } finally {
      setCarregandoTurmas(false);
    }
  };

  const handleCriar = async (values) => {
    const abertura = values.data_abertura;
    const encerramento = values.data_encerramento;
    const referencia = values.data_referencia;

    if (abertura.isAfter(encerramento, 'day')) {
      message.error('A data de disponibilização não pode ser posterior ao encerramento.');
      return;
    }
    if (referencia.isAfter(encerramento, 'day')) {
      message.error('A data-base não pode ser posterior ao encerramento.');
      return;
    }

    setCriando(true);
    try {
      const response = await avaliacoesAdminService.criarAvaliacaoComTurmas({
        nome: values.nome,
        data_referencia: referencia.format('YYYY-MM-DD'),
        data_abertura: abertura.format('YYYY-MM-DD'),
        data_encerramento: encerramento.format('YYYY-MM-DD'),
        turma_ids: values.turma_ids,
      });
      const totalInconsistencias = response.data.turmas.reduce(
        (soma, t) => soma + (t.inconsistencias?.length || 0),
        0
      );
      message.success(
        totalInconsistencias > 0
          ? `Avaliação criada com sucesso. ${totalInconsistencias} matrícula(s) com inconsistência foram sinalizadas.`
          : 'Avaliação criada com sucesso.'
      );
      fecharEResetar();
      onCriada(response.data.periodo.id);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível criar a avaliação.');
      message.error(mensagem);
    } finally {
      setCriando(false);
    }
  };

  return (
    <Modal
      title="Nova avaliação"
      open={open}
      onOk={() => form.submit()}
      onCancel={fecharEResetar}
      okText="Criar avaliação"
      confirmLoading={criando}
      width={640}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleCriar}>
        <Form.Item name="nome" label="Nome" rules={[{ required: true, message: 'Nome é obrigatório' }]}>
          <Input placeholder="Ex: Avaliação 2º Semestre 2026" />
        </Form.Item>

        <Form.Item
          name="data_referencia"
          label="Data-base dos alunos"
          help="Define quais alunos serão considerados em cada turma para esta avaliação."
          rules={[{ required: true, message: 'Data-base é obrigatória' }]}
        >
          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} onChange={handleMudarDataReferencia} />
        </Form.Item>

        <Form.Item
          name="turma_ids"
          label="Turmas"
          rules={[{ required: true, message: 'Selecione ao menos uma turma' }]}
        >
          <Select
            mode="multiple"
            placeholder={
              form.getFieldValue('data_referencia')
                ? 'Selecione as turmas'
                : 'Defina a data-base para ver as turmas disponíveis'
            }
            disabled={!turmasCandidatas.length && !carregandoTurmas}
            loading={carregandoTurmas}
            optionLabelProp="label"
            notFoundContent={carregandoTurmas ? <Spin size="small" /> : 'Nenhuma turma com aluno elegível para esta data.'}
          >
            {turmasCandidatas.map((turma) => (
              <Select.Option key={turma.id_turma} value={turma.id_turma} label={turma.nome_turma}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '2px 0' }}>
                  <Text strong>{turma.nome_turma}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {turma.nome_modalidade} • {turma.nome_equipe} • {turma.nome_unidade}
                  </Text>
                  <div>
                    <Tag color="blue">{turma.quantidade_elegiveis} elegível(is)</Tag>
                    {turma.quantidade_inconsistencias > 0 && (
                      <Tag color="warning">{turma.quantidade_inconsistencias} inconsistência(s)</Tag>
                    )}
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {erroTurmas && <Alert type="error" showIcon message={erroTurmas} style={{ marginBottom: 16 }} />}

        <Form.Item
          name="data_abertura"
          label="Disponível para professores em"
          rules={[{ required: true, message: 'Data de disponibilização é obrigatória' }]}
        >
          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="data_encerramento"
          label="Encerramento"
          rules={[{ required: true, message: 'Data de encerramento é obrigatória' }]}
        >
          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>

        <Text type="secondary" style={{ fontSize: 12 }}>
          Antes de "Disponível para professores em", os professores não veem a avaliação. Após "Encerramento", a
          avaliação continua consultável, mas não pode mais ser alterada.
        </Text>
      </Form>
    </Modal>
  );
};

export default NovaAvaliacaoModal;
